import {
  accountGiftSchema,
  streamerGiftListSchema,
  streamerGiftSchema,
  type AccountGift,
  type GiftCodeState,
  type GiftCodeView,
  type StreamerGift,
  type StreamerGiftList,
} from '@growup/site-shared';
import { Button, Card, Notice, StatusBadge, type StatusTone } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';

const day = new Intl.DateTimeFormat('pt-PT', { timeZone: 'Europe/Lisbon', dateStyle: 'short' });
const formatDay = (iso: string) => day.format(new Date(iso));

export const GIFT_STATE_LOOK: Record<GiftCodeState, { label: string; tone: StatusTone }> = {
  available: { label: 'Código por usar', tone: 'info' },
  in_order: { label: 'Numa encomenda por pagar', tone: 'warning' },
  used: { label: 'Camisola levantada', tone: 'success' },
  expired: { label: 'Código expirado', tone: 'neutral' },
};

/**
 * Streamers que ja fizeram um ano (camisola de streamer, docs/streaming). So
 * para o Gestor de Streaming; sem a permissao nao se pede nada.
 */
export function useStreamerGifts(enabled: boolean): {
  list: StreamerGiftList | null;
  reload: () => void;
} {
  const [list, setList] = useState<StreamerGiftList | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    getJson('/api/site/gifts/streamers', streamerGiftListSchema)
      .then(setList)
      // Uma ajuda, nao uma obrigacao: sem a site-api a lista de membros continua.
      .catch(() => undefined);
  }, [enabled, version]);
  return { list, reload: useCallback(() => setVersion((v) => v + 1), []) };
}

/** Quem ainda esta a espera da camisola. */
export const shirtDue = (list: StreamerGiftList | null): StreamerGift[] =>
  (list?.streamers ?? []).filter((s) => s.due && s.gift === null);

async function offer(accountId: string): Promise<StreamerGift> {
  return streamerGiftSchema.parse(await send('POST', `/api/site/gifts/streamers/${accountId}`));
}

/** No topo da lista de membros: quem fez um ano como streamer e ainda nao recebeu a camisola. */
export function StreamerShirtBanner({
  list,
  onOpen,
  onChanged,
}: {
  list: StreamerGiftList | null;
  onOpen: (accountId: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const due = shirtDue(list);
  if (!list || due.length === 0) return null;

  const give = async (accountId: string) => {
    setBusy(accountId);
    setError(null);
    try {
      await offer(accountId);
      onChanged();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      headingLevel={2}
      title="Camisola de streamer"
      eyebrow={due.length === 1 ? '1 por oferecer' : `${String(due.length)} por oferecer`}
      className="lg:col-span-2"
    >
      <p className="text-gu-ink-muted">
        Fizeram um ano como streamer da Grow uP. O código de oferta vale uma camisola grátis e vai
        por email ao streamer.
      </p>
      {list.product === null ? (
        <Notice tone="warning">
          Marque primeiro um produto como camisola de streamer em Loja › Produtos.
        </Notice>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <ul className="flex flex-col divide-y divide-gu-line">
        {due.map((s) => (
          <li key={s.accountId} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <button type="button" className="gu-link text-left" onClick={() => onOpen(s.accountId)}>
              <span className="font-semibold">{s.login ?? s.displayName}</span>
              <span className="text-caption text-gu-ink-muted">
                {' '}
                · {s.channel} · fez um ano a {formatDay(s.anniversary)}
              </span>
            </button>
            <Button
              size="sm"
              disabled={busy !== null || list.product === null}
              onClick={() => void give(s.accountId)}
            >
              {busy === s.accountId ? 'A gerar…' : 'Oferecer camisola'}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function GiftLine({ gift }: { gift: GiftCodeView }) {
  const look = GIFT_STATE_LOOK[gift.state];
  return (
    <div className="flex flex-col gap-2">
      <p>
        <StatusBadge tone={look.tone}>{look.label}</StatusBadge>
      </p>
      <p>
        Código <span className="font-mono font-semibold">{gift.code}</span> · {gift.productName}
      </p>
      <p className="text-caption text-gu-ink-muted">
        Gerado a {formatDay(gift.createdAt)}
        {gift.createdBy ? ` por ${gift.createdBy}` : ''} · vale até {formatDay(gift.expiresAt)}
        {gift.orderNumber ? ` · encomenda ${gift.orderNumber}` : ''}
      </p>
    </div>
  );
}

/** Na ficha: o ano de streamer, o codigo (se ja o tem) e o botao para o oferecer. */
export function StreamerShirtCard({
  accountId,
  onChanged,
}: {
  accountId: string;
  onChanged?: () => void;
}) {
  const [data, setData] = useState<AccountGift | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getJson(`/api/site/gifts/accounts/${accountId}`, accountGiftSchema)
      .then(setData)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [accountId]);

  if (error && !data) return <Notice tone="danger">Camisola de streamer: {error}</Notice>;
  if (!data) return null;
  const { streamer, gift } = data;
  if (!gift && !streamer) return null;

  const give = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await offer(accountId);
      setData({ ...data, streamer: updated, gift: updated.gift });
      onChanged?.();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Camisola de streamer">
      {gift ? (
        <GiftLine gift={gift} />
      ) : streamer?.due ? (
        <>
          <p>
            Fez um ano como streamer a {formatDay(streamer.anniversary)}. Ofereça a camisola: o
            código vai por email e vale uma vez.
          </p>
          <Button className="self-start" disabled={busy} onClick={() => void give()}>
            {busy ? 'A gerar…' : 'Gerar código e enviar'}
          </Button>
        </>
      ) : streamer ? (
        <p className="text-gu-ink-muted">
          Faz um ano como streamer a {formatDay(streamer.anniversary)}. Nessa altura aparece aqui o
          botão para oferecer a camisola.
        </p>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </Card>
  );
}
